import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@mui/material'
import useTranslation from '@/hooks/useTranslation'
import Markdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'

import { useAuthContext } from '@/features/auth/context'
import useChangelog from '@/hooks/useChangelog'

const formatDate = (dateString: string): string => {
  try {
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

const ChangelogPopup = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLogin } = useAuthContext()
  const { shouldShowPopup, latestVersion, markAsSeen } = useChangelog()

  const handleClose = () => {
    markAsSeen()
  }

  const handleViewAll = () => {
    markAsSeen()
    navigate('/dashboard/changelog')
  }

  if (!isLogin) return null
  if (!shouldShowPopup || !latestVersion) return null

  return (
    <Dialog
      open={shouldShowPopup}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: 'background.default' },
      }}
    >
      <Box sx={{ backgroundColor: 'background.default' }}>
        <Stack sx={{ padding: '41px 34px 4px 34px' }}>
          <Typography
            sx={{
              color: 'text.main',
              fontWeight: 400,
              fontSize: '20px',
              lineHeight: '24px',
            }}
          >
            {t('WhatsNewInVersion', { version: latestVersion.version })}
          </Typography>
          <Typography
            sx={{
              color: 'grey.600',
              fontSize: '12px',
              mt: 0.5,
            }}
          >
            {formatDate(latestVersion.date)}
          </Typography>
        </Stack>

        <DialogContent
          sx={{
            paddingLeft: '34px',
            paddingRight: '34px',
            paddingBottom: '18px',
            color: 'text.main',
            '& p': {
              color: 'text.main',
              fontSize: '12px',
              lineHeight: 1.6,
              margin: '4px 0',
            },
            '& h3': {
              color: 'text.main',
              fontSize: '14px',
              fontWeight: 700,
              mt: 2,
              mb: 1,
            },
            '& ul': {
              margin: 0,
              paddingLeft: '20px',
            },
            '& li': {
              color: 'text.main',
              fontSize: '12px',
              lineHeight: 1.6,
              mb: 0.5,
            },
            '& strong': {
              color: 'text.main',
            },
            '& blockquote': {
              margin: 0,
              borderLeft: '3px solid',
              borderColor: 'primary.main',
              paddingLeft: '12px',
              mb: 1,
            },
            '& blockquote p': {
              color: 'grey.600',
              fontSize: '13px',
              margin: 0,
            },
          }}
        >
          <Markdown>{latestVersion.content}</Markdown>
        </DialogContent>

        <DialogActions sx={{ paddingBottom: '32px', paddingRight: '30px' }}>
          <Button
            variant="secondary"
            sx={{ width: '140px' }}
            onClick={handleViewAll}
          >
            {t('ViewAllChanges')}
          </Button>
          <Button
            variant="primary"
            sx={{ width: '99px' }}
            onClick={handleClose}
          >
            {t('commonActionsClose')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default ChangelogPopup
